// **The one `DemoStageSpec` → C-1 `stage_json` translation** (`demo-presentation-shell` task 13.3).
//
// `stage-driver.ts`'s vocabulary is deliberately wire-agnostic; this is the single place it becomes
// C-1's `StageSpec` (`waggle-round/src/demo/stage.rs`). It used to be written twice — once in
// `demo.worker.ts` for production and once in `test/fake-demo-stage-driver.ts` for the tests — and
// both copies were wrong in the same way, which is exactly the failure two copies produce: neither
// emitted `seed`, a colony's `pool`/`cells`, or a patch's `pool`, all four of which C-1 **requires**
// (they carry no `#[serde(default)]`, and the struct is `deny_unknown_fields`). Against the real
// module every stage refused as `malformed_json`. One function now, consumed by both.
//
// The defaults below are the demo's own stage-prop values, not balance constants: a chapter that
// does not care what its starting pool is should not have to write one down, and a chapter that
// does (chapter 2's empty-programme harvesters) says so in `chapters.ts`.

import type { DemoStageSpec } from './stage-driver';

/**
 * `demo_configure`'s **second** argument for a stage — the name of an embedded preset, matching
 * `waggle-round`'s `NARRATION_PRESET` sentinel exactly, or `undefined` for the default `demo` one.
 *
 * A *name*, not a document: the presets live in `waggle-balance`, and a client shipping its own
 * copy of one would be a second source of truth for the physics. The `@` prefix cannot collide
 * with a preset TOML (which starts with a key or a table header), which is what lets one parameter
 * carry both without a mode flag.
 */
export function demoBalanceArg(stage: DemoStageSpec): string | undefined {
  return stage.balance === 'narration' ? '@narration' : undefined;
}

/**
 * **A buffed characteristic, spelled the way C-1 spells it.**
 *
 * C-1 resolves a buff with `stage::characteristic_by_name`, which matches
 * `waggle_bpp::ast::Characteristic::NAMES` — the snake_case B++ terminals (`capacity`,
 * `food_consumption`, …). Anything else is `malformed_stage`, with the colony's type registry
 * unchanged.
 *
 * The rounds colony read hands the client the *other* spelling: `/rounds/<scope>/colony` serialises
 * a type's buff as `format!("{:?}", …)` — Rust's `Debug`, so `Capacity` / `FoodConsumption` — and
 * `colonyMetaFromDto` passes that string through verbatim as `buffName`. Pushing it into
 * `create_type` is what refused every type the Beekeeper chapter's accepted document added, silently
 * (the create was refused, so the dependent hot-swap then refused too, for a type that was never
 * registered).
 *
 * So this is where the two spellings meet, in the one module that already owns "our vocabulary
 * becomes C-1's". Total and idempotent: an already-canonical name passes through unchanged, and an
 * unrecognised one is passed through as-is so C-1 refuses it loudly rather than this function
 * inventing a characteristic.
 */
export function c1BuffName(buff: string): string {
  return buff
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/[\s-]+/g, '_')
    .toLowerCase();
}

/** DESIGN §14's starting stores / cells, which is what a demo colony opens with unless a chapter
 * says otherwise. */
const DEFAULT_POOL = 100;
const DEFAULT_CELLS = 30;
/** DESIGN §14's per-flower richness — a staged patch at full bloom. */
const DEFAULT_PATCH_POOL = 212.5;
/** The demo's fixed seed. The stage is a prop, and a prop that differs between the rehearsal and
 * the show is not a rehearsal. */
const DEFAULT_SEED = 7;

export function demoStageJson(stage: DemoStageSpec): string {
  return JSON.stringify({
    seed: stage.seed ?? DEFAULT_SEED,
    colonies: stage.colonies.map((colony) => ({
      name: colony.name,
      birth_type: colony.birthType,
      types: colony.types.map((type) => ({
        name: type.name,
        buff: c1BuffName(type.buff),
        program: type.program,
      })),
      hive: colony.hive,
      pool: colony.pool ?? DEFAULT_POOL,
      cells: colony.cells ?? DEFAULT_CELLS,
      bees: colony.bees.map((bee) => ({ type: bee.type, position: bee.position })),
    })),
    patches: stage.patches.map((patch) => ({
      position: patch.position,
      pool: patch.pool ?? DEFAULT_PATCH_POOL,
    })),
  });
}
